import { Ng2StateDeclaration, StatesModule, Transition } from '@uirouter/angular';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType, ModuleType, ObjectTypes } from '@fi-shared/types';
import { FiChecklist2EditComponent } from '@fi-shared/checklist/fi-checklist2-edit/fi-checklist2-edit.component';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { FiChecklist2ListComponent } from '@fi-shared/checklist/fi-checklist2-list/fi-checklist2-list.component';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { AccessRightService } from '@fi-shared/fi-shared-services';
import { ChecklistV2ListNavigationTabsComponent } from '@fi-app/checklist/checklist-v2/checklist-v2-list-navigation-tabs/checklist-v2-list-navigation-tabs.component';
import { isDefined } from '@uirouter/core';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiChecklist2ScheduleService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule.service';
import { IChecklistV2, IChecklistV2Viewer } from '@fi-shared/checklist/checklist-v2';
import { FiChecklist2ViewerComponent } from '@fi-shared/checklist/fi-checklist2-viewer/fi-checklist2-viewer.component';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { cloneDeep } from '@fi-shared/utils';
import { FiChecklist2CheckpointsSectionEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoints-section-edit.component';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { FiCheckpointEditComponent } from '@fi-shared/checklist/fi-checklist2-edit/checkpoints-section-edit/checkpoint-edit/checkpoint-edit.component';
import {
    ChecklistV2_EditCheckpointResolve,
    ChecklistV2_EditCheckpointSectionsResolve,
    ChecklistV2Add_checklistFn,
    ChecklistV2EditCheckpointSectionsState_handleChecklistFn,
    onChecklistEditorStateExit,
} from '@fi-app/checklist/checklist-v2/checklist-v2-shared-resolvers';
import { FiChecklistEditorStoreService } from '@fi-shared/checklist/fi-checklist-editor-store.service';

const ChecklistV2State: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2._name,
    url: '/Checklist2',
    abstract: true,
    component: ChecklistV2ListNavigationTabsComponent,
};

export const checklistIdsResolveFn = [
    FiStateService,
    Transition,
    (fiStateService: FiStateService, tr: Transition) => fiStateService.getParams(tr).ids,
];
const ChecklistListV2State: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.List,
    url: '/?:filterType&:sortColumn&:sortDirection&:offset',
    params: {
        ids: null,
    },
    component: FiChecklist2ListComponent,
    reloadOnSearch: false,
    resolve: {
        ids: checklistIdsResolveFn,
    },
};

const ChecklistAddV2State: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Add._name,
    url: '/Add',
    params: {
        checklist: null,
        isAudit: false,
        isTemplateBased: false,
        templateMode: false,
    },
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    resolve: {
        accessRightFilterCollection: [AccessRightService, ChecklistV2Edit_accessRightFilterCollection],
        savedQuickSelectionFilters: [AccessRightService, ChecklistV2Edit_savedQuickSelectionFiltersResolveFn],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
        checklist: ChecklistV2Add_checklistFn,
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2EditState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Edit._name,
    url: '/{id:[0-9]+}/Edit',
    views: {
        '@Module': {
            component: FiChecklist2EditComponent,
        },
    },
    params: {
        isAudit: false,
        isTemplateBased: false,
        templateMode: false,
    },
    resolve: {
        checklist: [
            FiStateService,
            Transition,
            FiChecklist2Service,
            FiCheckpoint2HelperService,
            FiChecklistEditorStoreService,
            ChecklistV2Edit_checklist,
        ],
        accessRightFilterCollection: [AccessRightService, ChecklistV2Edit_accessRightFilterCollection],
        savedQuickSelectionFilters: [AccessRightService, ChecklistV2Edit_savedQuickSelectionFiltersResolveFn],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
    },
    onExit: onChecklistEditorStateExit,
};

const ChecklistV2EditCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Edit.EditCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: false,
        templateMode: false,
        isTemplateBased: false,
    },
    resolve: ChecklistV2_EditCheckpointSectionsResolve,
};

const ChecklistV2AddCheckpointSectionsState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Add.AddCheckpointSections._name,
    url: '/CheckpointSections',
    views: {
        '@Module': {
            component: FiChecklist2CheckpointsSectionEditComponent,
        },
    },
    params: {
        isAudit: false,
        templateMode: false,
        isTemplateBased: false,
    },
    resolve: {
        accessRightFilterCollection: [
            FiStateService,
            Transition,
            AccessRightService,
            ChecklistV2EditCheckpointSections_accessRightFilterCollection,
        ],
        isChatAvailable: [FiChatHelperService, ChecklistV2Edit_isChatAvailable],
        handleChecklist: ChecklistV2EditCheckpointSectionsState_handleChecklistFn,
    },
};

const ChecklistV2AddCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Add.AddCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: false,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

const ChecklistV2EditCheckpointSectionsEditCheckpointState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.Edit.EditCheckpointSections.EditCheckpoint,
    url: '/{checkpointGroupIndex:[0-9]+}/EditCheckpoint/{checkpointIndex:[0-9]+}',
    views: {
        '@Module': {
            component: FiCheckpointEditComponent,
        },
    },
    params: {
        isAudit: false,
    },
    resolve: ChecklistV2_EditCheckpointResolve,
};

export const ChecklistV2ViewState_checklistViewer = [
    FiStateService,
    'auditMode',
    Transition,
    FiChecklist2Service,
    (
        stateService: FiStateService,
        auditMode: boolean,
        transition: Transition,
        fiChecklist2Service: FiChecklist2Service,
    ) => {
        const checklistToScheduleId = coerceNumberProperty(stateService.getParams(transition).id);
        const businessUnitIdStr = stateService.getParams(transition).businessUnitId;
        const businessUnitId = isDefined(businessUnitIdStr) ? coerceNumberProperty(businessUnitIdStr) : undefined;
        const checklistPeriodIdStr = stateService.getParams(transition).checklistPeriodId;
        const checklistPeriodId = isDefined(checklistPeriodIdStr)
            ? coerceNumberProperty(checklistPeriodIdStr)
            : undefined;
        return fiChecklist2Service.getChecklistViewer(
            checklistToScheduleId,
            auditMode,
            businessUnitId,
            checklistPeriodId,
        );
    },
];

export const ChecklistV2ViewState_checklistViewerByChecklist = [
    FiStateService,
    'auditMode',
    Transition,
    FiChecklist2Service,
    (
        stateService: FiStateService,
        auditMode: boolean,
        transition: Transition,
        fiChecklist2Service: FiChecklist2Service,
    ) => {
        const checklistId = coerceNumberProperty(stateService.getParams(transition).id);
        return fiChecklist2Service.getChecklistViewerByChecklist(checklistId, auditMode);
    },
];

export const ChecklistV2ViewState_checklistViewerByChecklistTemplate = [
    FiStateService,
    'auditMode',
    Transition,
    FiChecklist2TemplateService,
    (
        stateService: FiStateService,
        auditMode: boolean,
        transition: Transition,
        fiChecklist2TemplateService: FiChecklist2TemplateService,
    ) => {
        const checklistId = coerceNumberProperty(stateService.getParams(transition).id);
        return fiChecklist2TemplateService.getChecklistViewerByChecklistTemplate(checklistId, auditMode);
    },
];

export const ChecklistV2ViewState_checklistUnitsSchedulePeriods = [
    'checklistViewer',
    'auditMode',
    FiChecklist2ScheduleService,
    (
        checklistViewer: IChecklistV2Viewer,
        auditMode: boolean,
        fiChecklist2ScheduleService: FiChecklist2ScheduleService,
    ) => {
        return fiChecklist2ScheduleService.getPeriodsByScheduleId(checklistViewer.ScheduleId, auditMode);
    },
];

export function setChecklistDataToStore(
    checklistData: IChecklistV2,
    fiCheckpoint2HelperService: FiCheckpoint2HelperService,
    fiChecklistEditorStoreService: FiChecklistEditorStoreService,
    skipSetInitial?: boolean,
) {
    checklistData.Names = fiCheckpoint2HelperService.getContentLanguageLabelModels(checklistData.Names);
    checklistData.Descriptions = fiCheckpoint2HelperService.getContentLanguageLabelModels(checklistData.Descriptions);

    if (!skipSetInitial) {
        fiChecklistEditorStoreService.setInitialChecklistData(checklistData);
    }

    const currentChecklist: IChecklistV2 = fiChecklistEditorStoreService.getCurrentChecklistData$().value;
    if (!isDefined(currentChecklist)) {
        fiChecklistEditorStoreService.setCurrentChecklistData$(cloneDeep(checklistData));
    }
}

export function ChecklistV2Edit_checklist(
    stateService: FiStateService,
    transition: Transition,
    fiChecklist2Service: FiChecklist2Service,
    fiCheckpoint2HelperService: FiCheckpoint2HelperService,
    fiChecklistEditorStoreService: FiChecklistEditorStoreService,
) {
    const checklistId: number = coerceNumberProperty(stateService.getParams(transition).id);
    return new Promise<void>((resolve, reject) => {
        fiChecklist2Service
            .getChecklist(checklistId, stateService.getParams(transition).isAudit)
            .then((checklistData: IChecklistV2) => {
                setChecklistDataToStore(checklistData, fiCheckpoint2HelperService, fiChecklistEditorStoreService);
                resolve();
            })
            .catch(reject);
    });
}

export function ChecklistV2Edit_accessRightFilterCollection(accessRightService: AccessRightService, isAudit?: boolean) {
    return accessRightService.getAccessRightsForPublicationByObjectType(
        !isAudit ? ObjectTypes.Checklist2 : ObjectTypes.Checklist2Audit,
    );
}

export function ChecklistV2Edit_savedQuickSelectionFiltersResolveFn(accessRightService: AccessRightService) {
    return accessRightService.GetSelectionFiltersForPublication(ModuleType.ChecklistV2, true);
}

export function ChecklistV2EditCheckpointSections_accessRightFilterCollection(
    stateService: FiStateService,
    transition: Transition,
    accessRightService: AccessRightService,
) {
    const accessRightFilterCollection = stateService.getParams(transition).accessRightFilterCollection;
    if (isDefined(accessRightFilterCollection)) return accessRightFilterCollection;
    const isAudit: boolean = coerceBooleanProperty(stateService.getParams(transition).isAudit);
    return ChecklistV2Edit_accessRightFilterCollection(accessRightService, isAudit);
}

export function ChecklistV2Edit_isChatAvailable(fiChatHelperService: FiChatHelperService) {
    return fiChatHelperService.hasAccessToChat();
}

export const ChecklistV2View_businessUnitId = [
    FiStateService,
    FiUserDataService,
    Transition,
    (stateService: FiStateService, userDataService: FiUserDataService, tr: Transition) => {
        const businessUnitId =
            coerceNumberProperty(stateService.getParams(tr).businessUnitId) ?? userDataService.getUserBusinessUnitId();
        return businessUnitId;
    },
];

export const ChecklistV2View_isChecklistTimeOut = [
    FiStateService,
    FiUserDataService,
    Transition,
    (stateService: FiStateService, userDataService: FiUserDataService, tr: Transition) => {
        const isChecklistTimeOut = coerceBooleanProperty(stateService.getParams(tr).isChecklistTimeOut);
        return isChecklistTimeOut;
    },
];

export const ChecklistV2View_isPreviewMode = [
    FiStateService,
    FiUserDataService,
    Transition,
    (stateService: FiStateService, userDataService: FiUserDataService, tr: Transition) => {
        const isPreviewMode = coerceBooleanProperty(stateService.getParams(tr).isPreviewMode);
        return isPreviewMode;
    },
];

export const ChecklistV2View_fromLinkTo = [
    FiStateService,
    FiUserDataService,
    Transition,
    (stateService: FiStateService, userDataService: FiUserDataService, tr: Transition) => {
        const fromLinkTo = coerceBooleanProperty(stateService.getParams(tr).fromLinkTo);
        return fromLinkTo;
    },
];

const ChecklistV2ViewState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.View,
    url: '/{id:[0-9]+}/View?{:businessUnitId}&{:checklistPeriodId}&{:isPreviewMode}&{:fromLinkTo}',
    params: {
        isChecklistTimeOut: null,
    },
    views: {
        '@Module': {
            component: FiChecklist2ViewerComponent,
        },
    },
    resolve: {
        checklistViewer: ChecklistV2ViewState_checklistViewer,
        checklistUnitsSchedulePeriods: ChecklistV2ViewState_checklistUnitsSchedulePeriods,
        businessUnitId: ChecklistV2View_businessUnitId,
        isChecklistTimeOut: ChecklistV2View_isChecklistTimeOut,
        isPreviewMode: ChecklistV2View_isPreviewMode,
        fromLinkTo: ChecklistV2View_fromLinkTo,
        auditMode: [() => false],
    },
};

const ChecklistV2ViewByChecklistState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.ViewByChecklist,
    url: '/{id:[0-9]+}/ViewByChecklist?{:fromLinkTo}',
    views: {
        '@Module': {
            component: FiChecklist2ViewerComponent,
        },
    },
    resolve: {
        checklistViewer: ChecklistV2ViewState_checklistViewerByChecklist,
        fromLinkTo: ChecklistV2View_fromLinkTo,
        auditMode: [() => false],
    },
};

const ChecklistV2ViewByChecklistTemplateState: Ng2StateDeclaration = {
    name: FiStateNames.ChecklistV2.ViewByChecklistTemplate,
    url: '/{id:[0-9]+}/ViewByChecklistTemplate',
    views: {
        '@Module': {
            component: FiChecklist2ViewerComponent,
        },
    },
    resolve: {
        checklistViewer: ChecklistV2ViewState_checklistViewerByChecklistTemplate,
        auditMode: [() => false],
    },
};

export const ChecklistV2States: StatesModule = {
    states: [
        ChecklistV2State,
        ChecklistListV2State,
        ChecklistAddV2State,
        ChecklistV2EditState,
        ChecklistV2EditCheckpointSectionsState,
        ChecklistV2AddCheckpointSectionsState,
        ChecklistV2AddCheckpointSectionsEditCheckpointState,
        ChecklistV2EditCheckpointSectionsEditCheckpointState,
        ChecklistV2ViewState,
        ChecklistV2ViewByChecklistState,
        ChecklistV2ViewByChecklistTemplateState,
    ],
};

export function registerChecklistV2StateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules(
        [FiStateNames.ChecklistV2._name, FiStateNames.ChecklistV2.List, FiStateNames.ChecklistV2.View],
        {
            SettingType: [ApplicationSettingsType.ChecklistV2Local],
            AccessType: AccessRightType.Read,
        },
    );

    stateAccessCheckService.registerStateRules(
        [FiStateNames.ChecklistV2.Template._name, FiStateNames.ChecklistV2.Template.List],
        {
            SettingType: [ApplicationSettingsType.ChecklistV2Local],
            AccessType: AccessRightType.Admin,
        },
    );

    stateAccessCheckService.registerStateRules(
        [FiStateNames.ChecklistV2.Schedule._name, FiStateNames.ChecklistV2.Schedule.List],
        {
            SettingType: [ApplicationSettingsType.ChecklistV2Local],
            AccessType: AccessRightType.Share,
        },
    );
}
